import { useState, useEffect, useRef } from "react";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { JobRequestCustomV1, MessageType } from "@/lib/store/Realtime/message.types";
import { CustomFormData, FormSelection, NEW_VENDOR_ID } from "../types";
import { ExtendedFormTemplate } from "@/dentlab/src/pages/form-builder/types/formTypes";
import { getOrCreateVendorOrg, stripFileData, uploadJobRequestFiles } from "../utils/job-request.utils";
import { sendVendorInviteEmail } from "@/lib/email";
import { SYSTEM_STANDARD_FORM } from "../constants/system-standard-form";
import { addRecent } from "@/lib/utils/recents";

interface UseJobRequestFormProps {
	vendors: string[];
	vendorName?: string;
	forms: Record<string, Record<string, ExtendedFormTemplate>>;
	fetchForms: (orgIds: string[]) => Promise<void>;
	onSuccess?: () => void;
	myOrgId: string | undefined;
	myOrgName: string | undefined;
	initialFormId?: string;
}

export const useJobRequestForm = ({
	vendors,
	vendorName,
	forms,
	fetchForms,
	onSuccess,
	myOrgId,
	myOrgName,
	initialFormId,
}: UseJobRequestFormProps) => {
	const [selection, setSelection] = useState<FormSelection>({
		orgId: "",
		formId: initialFormId || "",
	});

	const [customFormData, setCustomFormData] = useState<CustomFormData>({});
	const [loading, setLoading] = useState(false);
	const [newVendorEmail, setNewVendorEmail] = useState("");

	const vendorsKey = vendors.join(",");
	const fetchedVendorsRef = useRef("");

	useEffect(() => {
		if (vendors.length > 0 && vendorsKey !== fetchedVendorsRef.current) {
			fetchedVendorsRef.current = vendorsKey;
			fetchForms(vendors);
		}
	}, [vendorsKey]);

	useEffect(() => {
		if (vendors.length > 0 && !selection.orgId) {
			const orgId = vendors[0];
			const orgForms = forms[orgId] ? Object.values(forms[orgId]) : [];
			const firstPublishedForm = orgForms.find((f) => f.published_at);
			const formId = initialFormId || firstPublishedForm?.id || SYSTEM_STANDARD_FORM.id;
			setSelection({ orgId, formId });

			if (!initialFormId && !firstPublishedForm && vendorName) {
				addRecent({
					type: "job-request-form",
					vendorOrgId: orgId,
					vendorName,
					formId: SYSTEM_STANDARD_FORM.id,
					formTitle: SYSTEM_STANDARD_FORM.title,
					timestamp: Date.now(),
				});
			}
		}
	}, [vendors, selection.orgId, forms]);

	const handleFormSelect = (orgId: string, formId: string) => {
		setSelection({ orgId, formId });
		setCustomFormData({});

		// Reset vendor email if we select a different form 
		// Since different form implies known vendor
		setNewVendorEmail("");

		if (vendorName) {
			const formTitle =
				forms[orgId]?.[formId]?.title ?? SYSTEM_STANDARD_FORM.title;
			addRecent({
				type: "job-request-form",
				vendorOrgId: orgId,
				vendorName,
				formId,
				formTitle,
				timestamp: Date.now(),
			});
		}
	};

	const handleReset = () => {
		setCustomFormData({});
	};

	const createFormReferences = (
		form: ExtendedFormTemplate
	): Record<string, string> => {
		return form.definition.sections.reduce(
			(acc, section) => {
				section.fields.forEach((field) => {
					if (field.type !== "title" && field.type !== "description") {
						acc[field.id] = field.label;

						if (field.type === "odontogram" && field.odontogramFields) {
							field.odontogramFields.forEach((dropdownField) => {
								acc[dropdownField.id] = dropdownField.label;
								if (dropdownField.options) {
									dropdownField.options.forEach((option, index) => {
										acc[`${dropdownField.id}-${index}`] = option;
									});
								}
							});
						}

						if (field.type === "appointment" && field.appointmentFields) {
							field.appointmentFields.forEach((subField) => {
								acc[subField.id] = subField.label;
								if (subField.options) {
									subField.options.forEach((option, index) => {
										acc[`${subField.id}-${index}`] = option;
									});
								}
							});
						}
					}
				});
				return acc;
			},
			{} as Record<string, string>
		);
	};

	// Resolves the vendor org ID for submission:
	// - Existing vendor selected → return their org ID directly
	// - "Neues Fremdlabor" selected → create org from email, send invite, return new org ID
	const resolveVendorOrgId = async (): Promise<string | null> => {
		Logger.log("Resolving vendor org ID", { selection, newVendorEmail });

		// Existing vendor — use as-is
		if (selection.orgId !== NEW_VENDOR_ID && selection.orgId !== "") {
			Logger.log("Selected existing vendor org ID", { orgId: selection.orgId });
			return selection.orgId;
		}

		// New vendor — need an email to find or create their org
		if (!newVendorEmail.trim()) {
			showNotification({
				message: "E-Mail-Adresse ist erforderlich",
				type: "error",
			});
			return null;
		}

		const vendorOrgId = await getOrCreateVendorOrg(newVendorEmail);
		if (!vendorOrgId) {
			showNotification({
				message: "Fehler beim Erstellen der Fremdlabor-Organisation",
				type: "error",
			});
			return null;
		}

		if (myOrgName) {
			await sendVendorInviteEmail({
				recipientEmail: newVendorEmail,
				labName: myOrgName,
			});
		}

		return vendorOrgId;
	};

	const handleSubmit = async (parentJobId?: number) => {
		Logger.log("useJobRequestForm - handleSubmit", { parentJobId });
		if (!myOrgId) {
			showNotification({
				message: "Organisation ID fehlt",
				type: "error",
			});
			return;
		}

		if (!selectedForm) {
			showNotification({
				message: "Bitte wählen Sie ein Formular aus",
				type: "error",
			});
			return;
		}

		setLoading(true);

		try {
			const vendorOrgId = await resolveVendorOrgId();
			Logger.log("Resolved vendorOrgId", { vendorOrgId });
			if (!vendorOrgId) {
				// resolveVendor org shows notification
				setLoading(false);
				return;
			}

			const filePaths = await uploadJobRequestFiles(customFormData);

			if (
				filePaths.length === 0 &&
				Object.keys(customFormData).some((key) => {
					const field = customFormData[key];
					return (
						field?.fieldType === "file" &&
						field?.fieldValue &&
						Array.isArray(field.fieldValue) &&
						field.fieldValue.length > 0
					);
				})
			) {
				setLoading(false);
				return;
			}

			const isSystemForm = selectedForm.id === SYSTEM_STANDARD_FORM.id;

			const messageContent: JobRequestCustomV1 = {
				type: MessageType.JOB_REQUEST_CUSTOM_V1,
				incoming_order_date: new Date().toISOString(),
				customFormData: stripFileData(customFormData),
				customFormId: selectedForm.id,
				customFormTitle: selectedForm.title,
				customFormReferences: createFormReferences(selectedForm),
				parent_job_id: parentJobId,
			};

			const hasFiles = Object.values(customFormData).some(
					(field) =>
						field?.fieldType === "file" &&
						Array.isArray(field.fieldValue) &&
						field.fieldValue.length > 0
				);

			Logger.log("Submitting job request with content", { messageContent, filePaths });

			const { error: jobRequestError } = await supabase.rpc("request_job", {
				p_requesting_org_id: myOrgId,
				p_vendor_org_id: vendorOrgId,
				p_content: JSON.stringify(messageContent),
				p_title: "Neue Auftragsanfrage",
				p_file_paths: filePaths,
				p_parent_job_id: parentJobId,
				p_form_id: isSystemForm ? undefined : selection.formId,
				p_field_count: Object.keys(customFormData).length,
				p_has_files: hasFiles,
			});

			if (jobRequestError) {
				throw jobRequestError;
			}

			showNotification({
				message: "Auftrag erfolgreich erstellt",
				type: "success",
			});

			handleReset();
			setNewVendorEmail("");
			onSuccess?.();
		} catch (error) {
			Logger.error(error, {}, "Error submitting job request");
			showNotification({
				message: "Fehler beim Erstellen des Auftrags",
				type: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	const vendorForms = forms[selection.orgId];
	const selectedForm: ExtendedFormTemplate | null =
		vendorForms?.[selection.formId] ??
		(selection.formId === SYSTEM_STANDARD_FORM.id ? SYSTEM_STANDARD_FORM : null);

	return {
		selection,
		customFormData,
		loading,
		selectedForm,
		newVendorEmail,
		setNewVendorEmail,
		setCustomFormData,
		handleFormSelect,
		handleSubmit,
		handleReset,
	};
};
